import {
    BIG_NUMBER,
    FONT_DEFAULT_COLOR,
    SMALL_NUMBER,
} from '../../constants/render-constants';

import colorHelper from '../colors-helper';

const { getThreeColor, getBitmapColor } = colorHelper;

const fontFactory = ({ fontDefinition, colorNormalized }) => {
    // Character cache to speed up calculations
    let _cache;
    let _common;

    const _init = () => {
        _common = Object.assign({}, fontDefinition.common);
        _cache = {};
        const chars = fontDefinition.chars;

        _cache = Object.keys(chars).reduce((accum, key) => {
            const accumToReturn = accum;
            const char = chars[key];
            // For compatibility reasons, we have to use the
            // char.id as different generators will create slightly
            // different json files
            const keyText = char.id;
            const { width, height, xoffset, yoffset, xadvance } = char;
            const u0 = char.x / _common.scaleW;
            const v0 = 1 - char.y / _common.scaleH;
            const u1 = (char.x + width) / _common.scaleW;
            const v1 = 1 - (char.y + height) / _common.scaleH;

            accumToReturn[keyText] = {
                u0,
                v0,
                u1,
                v1,
                xoffset,
                yoffset,
                xadvance,
                width,
                height,
            };
            return accumToReturn;
        }, {});
    };

    // Adds vertices, indices and texturecoordinates as it also updates the box of the
    // text, and later adjusts all the offsets after using alignment. This way we
    // do not have to first measure and then generate coordinates. We cannot have a matrix
    // associated to the text either because we will add multiple texts in the same mesh object
    const addText = ({
        targetBox,
        size,
        text,
        color = FONT_DEFAULT_COLOR,
        alpha = 1,
        vertices,
        offsets,
        indices,
        colors,
        uv,
        z,
        rotate,
    }) => {
        const verticesToModify = vertices;
        const offsetsToModify = offsets;
        const colorsToModify = colors;
        const sizeFactor = size / _common.base;
        const min = {
            x: BIG_NUMBER,
            y: BIG_NUMBER,
        };

        const colorToUse = colorNormalized
            ? getThreeColor(color)
            : getBitmapColor(color);

        const max = {
            x: SMALL_NUMBER,
            y: SMALL_NUMBER,
        };
        let penPos = 0;

        const startIndex = verticesToModify.length;
        const startOffsetIndex = offsetsToModify.length;

        for (let i = 0; i < text.length; i++) {
            const charCode = text.charCodeAt(i);
            const fontChar = _cache[charCode];

            if (fontChar) {
                const {
                    xoffset,
                    yoffset,
                    width,
                    height,
                    xadvance,
                    u0,
                    v0,
                    u1,
                    v1,
                } = fontChar;
                let startX = penPos + xoffset;
                let endX = startX + width;
                let startY = yoffset;
                let endY = startY + height;

                startX *= sizeFactor;
                startY *= sizeFactor;
                endX *= sizeFactor;
                endY *= sizeFactor;
                if (startX < min.x) {
                    min.x = startX;
                }

                max.x = endX;

                if (startY < min.y) {
                    min.y = startY;
                }
                if (endY > max.y) {
                    max.y = endY;
                }
                penPos += xadvance;

                const firstVertexIndex = verticesToModify.length / 3;

                verticesToModify.push(
                    startX,
                    startY,
                    z,
                    endX,
                    startY,
                    z,
                    endX,
                    endY,
                    z,
                    startX,
                    endY,
                    z,
                );
                offsetsToModify.push(0, 0, 0);
                uv.push(u0, v0, u1, v0, u1, v1, u0, v1);
                colorsToModify.push(
                    colorToUse.r,
                    colorToUse.g,
                    colorToUse.b,
                    alpha,
                );
                colorsToModify.push(
                    colorToUse.r,
                    colorToUse.g,
                    colorToUse.b,
                    alpha,
                );
                colorsToModify.push(
                    colorToUse.r,
                    colorToUse.g,
                    colorToUse.b,
                    alpha,
                );
                colorsToModify.push(
                    colorToUse.r,
                    colorToUse.g,
                    colorToUse.b,
                    alpha,
                );
                // Leave + 0 below for better code readability
                indices.push(
                    firstVertexIndex + 0,
                    firstVertexIndex + 3,
                    firstVertexIndex + 1,
                    firstVertexIndex + 3,
                    firstVertexIndex + 2,
                    firstVertexIndex + 1,
                );
            }
        }

        const textBoxCenter = {
            x: (min.x + max.x) * 0.5,
            y: (min.y + max.y) * 0.5,
        };
        const targetCenter = {
            x: (targetBox.min.x + targetBox.max.x) * 0.5,
            y: (targetBox.min.y + targetBox.max.y) * 0.5,
        };

        const delta = {
            x: targetCenter.x - textBoxCenter.x,
            y: targetCenter.y - textBoxCenter.y,
        };

        for (
            let i = startIndex, j = startOffsetIndex;
            i < verticesToModify.length;
            i += 3, j += 2
        ) {
            if (!rotate) {
                offsetsToModify[j] = verticesToModify[i] - textBoxCenter.x;
                offsetsToModify[j + 1] =
                    verticesToModify[i + 1] - textBoxCenter.y;

                verticesToModify[i] = targetCenter.x;
                verticesToModify[i + 1] = targetCenter.y;
            } else {
                offsetsToModify[j] = 0;
                offsetsToModify[j + 1] = 0;
                verticesToModify[i] += delta.x;
                verticesToModify[i + 1] += delta.y;
            }
        }
        let endIndex = 0;

        if (verticesToModify.length) {
            endIndex = verticesToModify.length / 3 - 1;
        }

        return {
            startIndex: startIndex / 3,
            endIndex,
        };
    };

    // We initialise the character cache
    _init();

    return {
        addText,
    };
};

export default fontFactory;
